import React from 'react';
import { useContext, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Grid } from '@mui/material';
import { TextField } from 'formik-mui';
import { formatInTimeZone } from 'date-fns-tz';
import { Field, FormikProps, FormikProvider } from 'formik';
import DriveFileMoveIcon from '@mui/icons-material/DriveFileMove';
import * as Yup from 'yup';

import { ILayoutContext, LayoutContext, ModuleHandle, ToastSeverityEnum, useUderlyStore, Module, ModuleHostProps } from '@uderly/react-uderly-ui';
import useMediaImage from '../components/Module/hooks/useMediaImage';
import AppContext from '../AppContext';
import IqSpaceApi from '../../api/IqSpace';
import getPlaylistColumns from '../components/getPlaylistColumns';
import Separator from '../components/Separator';
import PlaylistQuestions from '../components/PlaylistQuestions';
import { hasPermission } from '../utils';
import PlaylistsSelect from '../components/PlaylistsSelect';
import { IqStore } from '../../models/IqStore';
import { StoreBreadcrumbs } from '../components/Module/StoreBreadcrumbs';

interface CollectionsProps extends ModuleHostProps {
  storeSubdomain: string;
}

const Collections = (props: CollectionsProps) => {
  const { t } = useTranslation();

  const layoutContext = useContext(LayoutContext) as ILayoutContext;

  const zustandStore = useUderlyStore(s => s.store);
  const user = useUderlyStore(s => s.user);

  const [store, setStore] = useState<IqStore | null>(zustandStore as IqStore);

  const userCanManageContent = () =>
    hasPermission(user, 'content_management');

  useEffect(() => {
    const init = async () => {
      const response = await IqSpaceApi.Store(props.storeSubdomain);

      if (response && response.status === 200) {
        setStore(response.data.data);
      }
    }

    init();
  }, [props.storeSubdomain]);

  const moduleData = {
    title: <StoreBreadcrumbs store={store} path="/collections" />,
    name: t('collections'),
    path: `/${store?.subdomain}/collections`,
    apiBasePath: '/collections'
  };

  const moduleRef = useRef<ModuleHandle>(null);

  const [item, setItem] = useState<any>(null);

  const mediaImage = useMediaImage(item, moduleRef, moduleData);

  const columns = getPlaylistColumns(moduleData.apiBasePath);

  const initialValues = {
    name: ""
  };

  const schema = () => {
    const v = {
      name: Yup.string().min(2).required((t('required-field') as string))
    };

    return Yup.object(v);
  }

  const onAddPackageToPlaylist = (pack) => {
    const onSelect = async (playlist) => {
      const response = await IqSpaceApi.AddPackageToPlaylist(playlist.id, pack.id);

      if (response.status === 202) {
        layoutContext.dialog.hide();
        layoutContext.toast.show({
          open: true,
          content: AppContext.r["added-to-playlist"] + " \"" + playlist.name + "\"",
          severity: ToastSeverityEnum.success
        });
        // } else if(response.status === 406) {
        //     setAlert({ open: true, message: AppContext.r["already-in-playlist"], severity: "warning" });
      } else {
        layoutContext.toast.show({
          open: true,
          content: "Error",
          severity: ToastSeverityEnum.error
        });
      }
    }

    layoutContext.dialog.show({
      title: <><DriveFileMoveIcon /> {AppContext.r["add-all-to-playlist"]}</>,
      content: <PlaylistsSelect onSelect={onSelect} />
    });
  }

  const onItemLoaded = (item: any) => {
    if (item && item.id) {
      setItem(item);
    }
  };

  const view = (_item: any, formik: FormikProps<any>) => {
    const { values } = formik;

    if (!store) return null;

    return (
      <FormikProvider value={formik}>
        <Grid container>
          <Grid container item spacing={1} sm md lg xl>
            <Grid item xs={12} sm={8} xl={12}>
              <Field component={TextField} name="name" type="text" readonly
                label={AppContext.r["name"] + "*"}
                inputProps={
                  { readOnly: !userCanManageContent(), }
                }
              />
            </Grid>

            {values.created_at &&
              <Grid item xs={12} sm={4} xl={4}>
                <div className="outlined-with-label disabled">
                  {formatInTimeZone(values.created_at, values.timezone, "dd/MM/yyyy H:mm:ss")}
                  <label>{AppContext.r["created-at"]}</label>
                </div>
              </Grid>}
          </Grid>

          <Grid item xs={12} sm={3} padding={1} className='media-image-container'>
            {item && mediaImage.renderComponent(formik, { apiPath: moduleData.apiBasePath, viewOnly: !userCanManageContent() })}
          </Grid>
        </Grid>

        <div className="questions-view">
          {(values && values.questions) &&
            <>
              <h3 className="text-center">{values.questions_count} {AppContext.r["questions"]}</h3>
              <Separator />

              <div className="top-menu">
                <Button variant="outlined"
                  onClick={() => onAddPackageToPlaylist(values)}>{AppContext.r["add-all-to-playlist"]}</Button>
              </div>

              <PlaylistQuestions playlist={values} disableSortable asCollection />
            </>}
        </div>
      </FormikProvider >
    );
  };

  return (
    <Module
      ref={moduleRef}
      className='playlists'
      columns={columns}
      schema={schema}
      view={view}
      data={moduleData}
      initialValues={initialValues}
      initialFilters={{ store_id: store?.id, language_id: user?.language_id }}
      onItemLoaded={onItemLoaded}
    />
  );
}

export default Collections;